import React from 'react';
import { 
  Zap, 
  Clock, 
  MessageSquare, 
  AlertCircle,
  TrendingUp,
  Package
} from 'lucide-react';

const SubscriptionStatus = ({ status, plan, onTopUp }) => {
  if (!status) return null;

  const {
    limit,
    usage,
    remaining,
    percentUsed,
    expiryDate,
    isExpired
  } = status;

  const getStatusColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-orange-500';
    return 'bg-emerald-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {/* Usage Summary Card */}
      <div className="md:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <MessageSquare className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Message Quota</h3>
              <p className="text-2xl font-black text-gray-900">{usage.toLocaleString()} / {limit.toLocaleString()}</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter ${isExpired ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
              {isExpired ? 'Expired' : 'Active'}
            </span>
          </div>
        </div>

        <div className="space-y-2 mt-auto">
          <div className="flex justify-between items-end mb-1">
            <span className="text-xs font-bold text-gray-500">Usage Progress</span>
            <span className="text-xs font-black text-gray-900">{percentUsed}%</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ease-out rounded-full ${getStatusColor(percentUsed)}`}
              style={{ width: `${Math.min(percentUsed, 100)}%` }}
            ></div>
          </div>
          <p className="text-[11px] text-gray-400 font-medium">
            You have <span className="text-primary-600 font-bold">{remaining.toLocaleString()}</span> messages remaining in your current cycle.
          </p>
        </div>
      </div>

      {/* Plan & Expiry Card */}
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-secondary-50 rounded-lg">
              <Package className="h-4 w-4 text-secondary-600" />
            </div>
            <div>
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-1">Current Plan</h3>
              <p className="text-sm font-extrabold text-gray-800">{plan?.plan?.name || 'Standard Plan'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
            <Clock className="h-3.5 w-3.5" />
            <span>Expires on: <b className="text-gray-900">{formatDate(expiryDate)}</b></span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary-600 to-secondary-700 rounded-2xl p-5 text-white shadow-lg shadow-primary-200">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-yellow-300" />
            <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Quick Action</span>
          </div>
          <h4 className="text-sm font-bold mb-1">Need more capacity?</h4>
          <p className="text-[11px] opacity-90 mb-4 font-medium leading-relaxed">Top up your account with instant message credits to keep your campaigns running.</p>
          <button 
            onClick={onTopUp}
            className="w-full py-2 bg-white text-primary-700 rounded-xl text-[11px] font-black uppercase tracking-wider hover:bg-primary-50 transition-colors shadow-sm"
          >
            Instant Top-up
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;
